import { EventListener, type HttpClient } from "silver";
import { type MyonApiResponse, isMyonApiSuccess } from "shared/Api/MyonApiResponse";

export interface BookBundleInterface {
	readonly id: number;
	title: string;
	readonly isDefault: boolean;
	readonly created: number;
	readonly userId: number;
	promotionExpiration: number;
}

type BookBundlePayload = {
	"default": number;
	bundles: {
		[bundleId: number]: {
			bundle: BookBundleInterface,
			books: number[];
		}
	}
};

function getResults<T>(response: MyonApiResponse<T>): T {
	if (!isMyonApiSuccess(response)) {
		console.error(response);
		throw new Error("Invalid response");
	}
	return response.result;
}

export class BookBundleService {

	public readonly onBeforeFavoriteUpdate = EventListener.create<{ bookId: number, favorited: boolean, sender: any }>("beforeFavoriteUpdate");
	public readonly onFavoriteUpdate = EventListener.create<{ bookId: number, favorited: boolean, sender: any }>("favoriteUpdate");

	private cachedBookBundleData?: BookBundlePayload;
	private lastBundleReady: Promise<void> = Promise.resolve();

	constructor(
		private httpClient: HttpClient
	) { }

	public async createBundle(title: string): Promise<BookBundleInterface> {
		if (title.length === 0) {
			throw new Error("Cannot create a new bundle");
		}

		const response: MyonApiResponse<BookBundleInterface> = await this.httpClient.post("/api/bookbundle", {
			body: { title }
		});
		const bundle = getResults(response);
		if (this.cachedBookBundleData) {
			this.cachedBookBundleData.bundles[bundle.id] = { bundle, books: [] };
		}

		return bundle;
	}

	public setBookIdsInFavorites(bookIds: number[], favorite: boolean, sender?: any): Promise<void> {
		return this.setBookIdsInBundle(bookIds, "default", favorite, sender);
	}

	public setBookIdsInBundle(bookIds: number[], bundleId: number|"default", inBundle: boolean, sender?: any): Promise<void> {
		// update the cache ASAP (if loaded)
		if (this.cachedBookBundleData) {
			const cacheBundleId = bundleId === "default" ? this.cachedBookBundleData.default : bundleId;
			if (!(cacheBundleId in this.cachedBookBundleData.bundles)) {
				throw new Error("Unknown bundle");
			}
			const bundle = this.cachedBookBundleData.bundles[cacheBundleId];
			bookIds.forEach(bookId => {
				const index = bundle.books.indexOf(bookId);
				if (inBundle && index === -1) {
					bundle.books.push(bookId);
				} else if (!inBundle && index >= 0) {
					bundle.books.splice(index, 1);
				}
			});
		}
		const isDefault = (bundleId === "default" || bundleId === this.cachedBookBundleData?.default);
		if (isDefault) {
			// dispatch the "before favorite update" event ASAP, for controls to update
			bookIds.forEach(bookId => this.onBeforeFavoriteUpdate.trigger({ bookId, favorited: inBundle, sender }));
		}

		const prevLastReady = this.lastBundleReady;
		return this.lastBundleReady = (async () => {
			await prevLastReady;
			const url = `/api/bookbundle/${bundleId}/books`;
			const response = await this.httpClient.ajax(url, {
				body:   { bookIds },
				method: inBundle ? "POST" : "DELETE"
			});
			if (!isMyonApiSuccess(response)) {
				console.error(response);
				this.lastBundleReady = Promise.resolve(); // reset chain
				throw new Error("request failed");
			}
			if (isDefault) {
				bookIds.forEach(bookId => this.onFavoriteUpdate.trigger({ bookId, favorited: inBundle, sender }));
			}
			return response.result;
		})();
	}

	public async deleteBundle(bundleId: number): Promise<void> {
		if (this.cachedBookBundleData) {
			delete this.cachedBookBundleData.bundles[bundleId];
		}
		getResults(await this.httpClient.delete(`/api/bookbundle/${bundleId}`));
	}

	public async updateBundle(bookBundle: BookBundleInterface): Promise<void> {
		if (bookBundle.isDefault) {
			throw new Error("Cannot modify default bundle");
		}
		const body = {
			title:                bookBundle.title,
			promotion_expiration: bookBundle.promotionExpiration
		};
		getResults(await this.httpClient.post(`/api/bookbundle/${bookBundle.id}`, { body }));
	}

	public async getBundleData(): Promise<BookBundlePayload> {
		if (this.cachedBookBundleData) {
			return this.cachedBookBundleData;
		}

		return this.cachedBookBundleData = await getResults(await this.httpClient.get("/api/bookbundle/"));
	}
}
